import { B2cProductSearchPayload, UrlDataDocument } from "@/generated/graphql.lazy/b2c";
import type { Services } from "./services";
import { createProductPath } from "@/util/category.ts";
import { defaultLang, supportedLanguages } from "@/constants/languages.ts";
import { Url } from "@/helpers/url.ts";

export type RegistryItem = { type: 'product' | 'category' | 'brand', id: string };

export class Routes {
    settings = {
        rootCategoryConverted: false
    }
    registry: Map<string, RegistryItem> = new Map();
    alternates: Map<string, string[]> = new Map();

    constructor(protected services: Services) {

    }

    async load() {
        this.prepareForBrands();
        this.prepareForCategories();
    }

    clear() {
        this.registry.clear();
        this.alternates.clear();
    }

    prepareForBrands() {
        for (const brand of this.services.brands.list) {
            const route = this.convertToRoute({
                segments: [brand.slug]
            });
            this.setForUrl(route, { type: 'brand', id: brand.id });
        }
    }

    prepareForCategories() {
        //const passenger = this.services.categories.getItem('passenger')!;
        for (const key of this.services.categories.getAll()) {
            if (key === 'passenger') {
                continue;
            }
            const path = this.services.categories.getItemPath(key)!;
            const route = this.convertToRoute({
                segments: [...path]
            });
            this.setForUrl(route, { type: 'category', id: key });
        }
    }

    prepareForProduct(product: B2cProductSearchPayload) {
        const path = createProductPath(product, this.services.categories);
        const route = this.convertToRoute({
            segments: path
        });
        this.setForUrl(route, { type: 'product', id: product.id });
    }

    async resolve(url: string, pageLang?: string, fresh = false) {
        if (!this.registry.has(url) || fresh) {
            await this.updateRegistryForUrl(url, pageLang);
        }

        return this.resolveFromRegistry(url);
    }

    async resolveAlternates(url: string, pageLang?: string) {
        if (!this.alternates.has(url)) {
            await this.updateRegistryForUrl(url, pageLang);
        }

        return this.alternates.get(url)!;
    }

    convertToRoute(params: { segments: string[] }) {
        const prefix = this.services.parameters.parameters.language;
        const routeSegments = [] as string[];

        routeSegments.push(prefix);
        routeSegments.push(...params.segments);

        return routeSegments.join('/');
    }

    async resolveFromLocalPath(segments: string[]) {
        return this.resolve(this.convertToRoute({
            segments
        }));
    }

    async updateRegistryForUrl(url: string, pageLang?: string) {
        const details = await this.queryDetailsForUrl(url, pageLang);

        if (details) {
            this.registry.set(url, details);
            this.setAlternates(url, details.alternates);
        }
    }

    resolveFromRegistry(url: string) {
        return this.registry.get(url);
    }

    setForUrl(url: string, details: { type: 'product' | 'category' | 'brand', id: string }) {
        this.registry.set(url, details);
    }

    setAlternates(url: string, alternates: string[]) {
        this.alternates.set(url, alternates);
    }

    protected async queryDetailsForUrl(url: string, pageLang?: string) {
        const response = await this.services.api.client.query({
            query: UrlDataDocument,
            variables: {
                input: {
                    url: url,
                    pageLang: pageLang,
                }
            }
        });

        if (response.errors?.length || !response.data.UrlData?.type) {
            const location = this.services.router.getCurrentRouteLocation();
            const language = location?.params?.lang ?? defaultLang;

            let url = location?.path ?? "/";

            if (!url.endsWith('/')) {
                url = url + '/';
            }

            const prefix = `/${language}/`;
            const alternates = this.services.customerChannel.languages.map((language) => {
              let alternate;
              if (url !== "/") {
                alternate =  Url.normalizeRoute(url.replace(prefix, `/${language}/`));
              } else {
                alternate =  Url.normalizeRoute(`/${language}/`);
              }

              if (alternate.endsWith('/')) {
                alternate = alternate.slice(0, -1);
              }

              return alternate;
            });

            return {
              type: 'static',
              id: undefined,
              alternates: alternates,
            };
        }

        const type = response.data.UrlData.type as 'product' | 'category' | 'brand';
        const alternates = response.data.UrlData.alternateUrls ?? [];

        if (type === 'category') {
            this.services.categories.addAdditionalInfoItem({
                id: response.data.UrlData.category.id,
                name: response.data.UrlData.category.name,
                description: response.data.UrlData.category.description,
            });
        }

        return {
            type: type,
            id: response.data.UrlData[type].id,
            alternates: alternates
        };
    }
}